
import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormValues) => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        data.email,
        {
          redirectTo: `${window.location.origin}/reset-password?fromEmail=true`,
        }
      );

      if (error) {
        console.error("Password reset error:", error);
        setError(error.message);
      } else {
        setIsEmailSent(true);
        toast({
          title: "Reset email sent!",
          description: "Check your email for a password reset link.",
        });
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex flex-col md:flex-row">
        <div className="hidden md:flex md:w-1/2 bg-emu-blue items-center justify-center p-0">
          <div className="w-full h-full flex items-center justify-center">
            <img 
              src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
              alt="EMU Logo" 
              className="w-full h-full object-cover" 
            />
          </div>
        </div>
        
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 md:p-10">
          <div className="w-full max-w-md text-center">
            <div className="flex md:hidden justify-center mb-8">
              <img 
                src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
                alt="EMU Logo" 
                className="h-14" 
              />
            </div>
            <h1 className="text-2xl font-bold text-emu-blue mb-4">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
            </p>
            <Link 
              to="/login" 
              className="text-emu-blue hover:underline"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 bg-emu-blue items-center justify-center p-0">
        <div className="w-full h-full flex items-center justify-center">
          <img 
            src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
            alt="EMU Logo" 
            className="w-full h-full object-cover" 
          />
        </div>
      </div>
      
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 md:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex md:hidden justify-center mb-8">
              <img 
                src="/lovable-uploads/6a083781-367a-4287-9348-47d49c383451.png" 
                alt="EMU Logo" 
                className="h-14" 
              />
            </div>
            <h1 className="text-2xl font-bold text-emu-blue mb-2">Forgot Password?</h1>
            <p className="text-sm text-gray-600 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="your@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full bg-emu-blue hover:bg-emu-darkblue text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-emu-blue hover:underline"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
